import React, { useState, useEffect } from "react";
import { Table, Button, Dropdown, Menu, Switch, Popover } from "antd";
import { Download, TrendingUp, Filter } from "react-feather";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from "chart.js";
import PartnerService from "../../utils/partnerService";
import Select from "react-select";
import moment from "moment";
import DatePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import exportService from "../../utils/exportService";
import Swal from "sweetalert2";
import PrintReportPreview from "../../reports/PrintReportPreview";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);


const OTARevenuePerformanceReport = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [bookingSourceData, setBookingSourceData] = useState([]);
  const [applicableTaxes, setApplicableTaxes] = useState([]);
const apiURL = process.env.REACT_APP_API_URL || "http://localhost:4000";


  const [dateRange, setDateRange] = useState({
    startDate: moment().startOf("day"),
    endDate: moment().startOf("day"),
  });
  const [otaPlatformFilter, setOtaPlatformFilter] = useState(null);
  const [showGraph, setShowGraph] = useState(true);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportFooter, setExportFooter] = useState([]);
  const [totalsForPageFooter, setTotalsForPageFooter] = useState([]);
  const [totalColumns, setTotalColumns] = useState([]);
  const [reportTitle, setReportTitle] = useState(t("ota_revenue_performance"));
  const currency = localStorage.getItem("currencycode") || "₹";
  const { getPartnerName } = PartnerService();

  useEffect(() => {
    const fetchBookingTypeSummaryData = async () => {
      if (!dateRange.startDate || !dateRange.endDate || !moment(dateRange.startDate).isValid() || !moment(dateRange.endDate).isValid()) {
        Swal.fire({
          title: t("error"),
          text: t("invalid_date_range"),
          icon: "error",
        });
        setBookingSourceData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await axios.get(`${apiURL}/reports-daily/booking-type-summary`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            startDate: dateRange.startDate.format("YYYY-MM-DD"),
            endDate: dateRange.endDate.format("YYYY-MM-DD"),
            page: 1,
            pageSize: 100,
          },
        });

        if (response.data.success) {
          // Filter out booking source IDs 0 and 1
          const filteredData = (response.data.report || []).filter(
            (item) => item.booking_source !== "0" && item.booking_source !== "1"
          );
          setBookingSourceData(filteredData);
          setApplicableTaxes(response.data.applicableTaxes || []);
        } else {
          throw new Error(response.data.message || "Failed to fetch OTA Revenue Performance Report");
        }
      } catch (error) {
        console.error("Error fetching OTA Revenue Performance Report:", error.message);
        Swal.fire({
          title: t("error"),
          text: t("failed_fetch_ota_revenue_report"),
          icon: "error",
        });
        setBookingSourceData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingTypeSummaryData();
  }, [dateRange, apiURL]);

  const columns = [
    {
      title: t("ota_partner_name"),
      dataIndex: "booking_source",
      key: "booking_source",
      render: (source) => (
        <span className="colored-box source-box">{getPartnerName(source) || source || "N/A"}</span>
      ),
      sorter: (a, b) => (a.booking_source || "").localeCompare(b.booking_source || ""),
    },
    {
      title: t("room_sold_count"),
      dataIndex: "room_sold_count",
      key: "room_sold_count",
      sorter: (a, b) => (a.room_sold_count || 0) - (b.room_sold_count || 0),
      render: (value) => (value ? value.toLocaleString() : ""),
    },
    {
      title: t("total_nights"),
      dataIndex: "total_nights",
      key: "total_nights",
      sorter: (a, b) => (a.total_nights || 0) - (b.total_nights || 0),
      render: (value) => (value ? value.toLocaleString() : ""),
    },
    {
      title: t("actual_price"),
      dataIndex: "actual_price",
      key: "actual_price",
      render: (text) => (text ? `${currency}${Number(text).toFixed(2)}` : ""),
      sorter: (a, b) => (a.actual_price || 0) - (b.actual_price || 0),
    },
    {
      title: t("commission_discount"),
      dataIndex: "discount",
      key: "discount",
      render: (text) => (text ? `${currency}${Number(text).toFixed(2)}` : ""),
      sorter: (a, b) => (a.discount || 0) - (b.discount || 0),
    },
    ...applicableTaxes.map((taxName) => ({
      title: `${taxName} (${currency})`,
      dataIndex: taxName,
      key: taxName,
      render: (text) => (text ? `${currency}${Number(text).toFixed(2)}` : ""),
      sorter: (a, b) => (a[taxName] || 0) - (b[taxName] || 0),
    })),
    {
      title: t("total_tax"),
      dataIndex: "total_tax",
      key: "total_tax",
      render: (text) => (text ? `${currency}${Number(text).toFixed(2)}` : ""),
      sorter: (a, b) => (a.total_tax || 0) - (b.total_tax || 0),
    },
    {
      title: t("net_revenue"),
      dataIndex: "net_revenue",
      key: "net_revenue",
      render: (text) => (text ? `${currency}${Number(text).toFixed(2)}` : ""),
      sorter: (a, b) => (a.net_revenue || 0) - (b.net_revenue || 0),
      onCell: () => ({ style: { backgroundColor: "#e2e6ea" } }),
    },
  ];

  const totals = bookingSourceData.reduce(
    (acc, item) => ({
      room_sold_count: acc.room_sold_count + (item.room_sold_count || 0),
      total_nights: acc.total_nights + (item.total_nights || 0),
      actual_price: acc.actual_price + (item.actual_price || 0),
      discount: acc.discount + (item.discount || 0),
      total_tax: acc.total_tax + (item.total_tax || 0),
      net_revenue: acc.net_revenue + (item.net_revenue || 0),
      ...applicableTaxes.reduce((taxAcc, taxName) => ({
        ...taxAcc,
        [taxName]: (taxAcc[taxName] || 0) + (item[taxName] || 0),
      }), acc),
    }),
    {
      room_sold_count: 0,
      total_nights: 0,
      actual_price: 0,
      discount: 0,
      total_tax: 0,
      net_revenue: 0,
      ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: 0 }), {}),
    }
  );

  const filteredData = bookingSourceData.filter(
    (item) => (otaPlatformFilter ? getPartnerName(item.booking_source) === otaPlatformFilter.value : true)
  );

  const barChartData = {
    labels: filteredData.map((item) => getPartnerName(item.booking_source) || item.booking_source || "N/A"),
    datasets: [
      {
        type: "bar",
        label: t("net_revenue"),
        data: filteredData.map((item) => item.net_revenue || 0),
        backgroundColor: "#28a745",
        yAxisID: "y",
      },
      {
        type: "bar",
        label: t("room_sold_count"),
        data: filteredData.map((item) => item.room_sold_count || 0),
        backgroundColor: "#007bff",
        yAxisID: "y1",
      },
      {
        type: "line",
        label: t("room_sold_count_trend"),
        data: filteredData.map((item) => item.room_sold_count || 0),
        borderColor: "#ff6347",
        backgroundColor: "#ff6347",
        fill: false,
        tension: 0.4,
        yAxisID: "y1",
      },
      {
        type: "line",
        label: t("total_nights"),
        data: filteredData.map((item) => item.total_nights || 0),
        borderColor: "#ffc107",
        backgroundColor: "#ffc107",
        fill: false,
        tension: 0.4,
        yAxisID: "y1",
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: { display: true, text: `${t("net_revenue")} (${currency})` },
        beginAtZero: true,
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        title: { display: true, text: t("count") },
        beginAtZero: true,
        grid: { drawOnChartArea: false },
      },
      x: {
        title: { display: true, text: t("ota_partner_name") },
      },
    },
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: t("ota_revenue_performance_chart") },
    },
  };

  const handleExport = (format) => {
    if (!filteredData.length && format !== "print") {
      Swal.fire({
        title: t("error"),
        text: t("no_data_available_for_export"),
        icon: "error",
      });
      return;
    }

    const exportData = filteredData.map((item) => ({
      booking_source: getPartnerName(item.booking_source) || item.booking_source || "N/A",
      room_sold_count: item.room_sold_count || 0,
      total_nights: item.total_nights || 0,
      actual_price: `${currency}${Number(item.actual_price || 0).toFixed(2)}`,
      discount: `${currency}${Number(item.discount || 0).toFixed(2)}`,
      ...applicableTaxes.reduce((acc, taxName) => ({
        ...acc,
        [taxName]: `${currency}${Number(item[taxName] || 0).toFixed(2)}`,
      }), {}),
      total_tax: `${currency}${Number(item.total_tax || 0).toFixed(2)}`,
      net_revenue: `${currency}${Number(item.net_revenue || 0).toFixed(2)}`,
    }));

    const exportHeaders = columns.map((col) => ({ key: col.key, label: col.title }));

    const exportFooter = [
      { key: "total", value: t("total") },
      { key: "room_sold_count", value: totals.room_sold_count, currency: false },
      { key: "total_nights", value: totals.total_nights, currency: false },
      { key: "actual_price", value: `${currency}${totals.actual_price.toFixed(2)}`, currency: true },
      { key: "discount", value: `${currency}${totals.discount.toFixed(2)}`, currency: true },
      ...applicableTaxes.map((taxName) => ({
        key: taxName,
        value: `${currency}${totals[taxName].toFixed(2)}`,
        currency: true,
      })),
      { key: "total_tax", value: `${currency}${totals.total_tax.toFixed(2)}`, currency: true },
      { key: "net_revenue", value: `${currency}${totals.net_revenue.toFixed(2)}`, currency: true },
    ];

    const filename = `ota_revenue_performance_report_${dateRange.startDate.format("YYYY-MM-DD")}_${dateRange.endDate.format("YYYY-MM-DD")}`;

    if (format === "xls") {
      exportService.exportToXLS(exportData, exportHeaders, `${filename}.xlsx`, t("ota_revenue_performance"));
    } else if (format === "csv") {
      exportService.exportToCSV(exportData, exportHeaders, `${filename}.csv`);
    } else if (format === "pdf") {
      exportService.exportToPDF(
        exportData,
        exportHeaders,
        t("ota_revenue_performance"),
        `${filename}.pdf`,
        false,
        true,
        exportFooter,
        exportFooter
      );
    } else if (format === "preview") {
      setExportData(exportData);
      setExportHeaders(exportHeaders);
      setExportFooter(exportFooter);
      setTotalsForPageFooter(exportFooter);
      setTotalColumns(["room_sold_count", "total_nights", "actual_price", "discount", ...applicableTaxes, "total_tax", "net_revenue"]);
      setPreviewVisible(true);
    } else if (format === "print") {
      window.print();
    }
  };

  const otaPlatformOptions = filteredData
    .map((item) => ({
      value: getPartnerName(item.booking_source),
      label: getPartnerName(item.booking_source) || "N/A",
    }))
    .filter((option, index, self) => index === self.findIndex((t) => t.value === option.value));

  const handleDateRangeChange = (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        if (param === "prev") {
          newRange = {
            startDate: prevRange.startDate.clone().subtract(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().subtract(1, "day").startOf("day"),
          };
        } else if (param === "next") {
          newRange = {
            startDate: prevRange.startDate.clone().add(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().add(1, "day").startOf("day"),
          };
        } else {
          return prevRange;
        }
      } else {
        const { start, end } = param;
        newRange = {
          startDate: moment(start).startOf("day"),
          endDate: moment(end).startOf("day"),
        };
      }

      if (!newRange.startDate.isValid() || !newRange.endDate.isValid()) {
        Swal.fire({
          title: t("error"),
          text: t("invalid_date_selected"),
          icon: "error",
        });
        setBookingSourceData([]);
        return prevRange;
      }

      if (newRange.startDate.isAfter(newRange.endDate)) {
        Swal.fire({
          title: t("error"),
          text: t("start_date_after_end_date"),
          icon: "error",
        });
        setBookingSourceData([]);
        return prevRange;
      }

      return newRange;
    });
  };

  const handleOtaPlatformFilterChange = (option) => {
    setOtaPlatformFilter(option);
  };

  const filterPopupContent = (
    <div className="p-4" style={{ width: "250px" }}>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t("filter_by_ota_partner")}</label>
        <Select
          value={otaPlatformFilter}
          onChange={handleOtaPlatformFilterChange}
          options={otaPlatformOptions}
          isClearable
          placeholder={t("filter_by_ota_partner")}
          styles={{
            container: (provided) => ({ ...provided, width: "100%" }),
            control: (provided) => ({
              ...provided,
              minHeight: "32px",
              borderRadius: "4px",
              borderColor: "#d9d9d9",
              background: "#fff",
            }),
            menu: (provided) => ({ ...provided, width: "100%" }),
          }}
        />
      </div>
    </div>
  );

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={() => handleExport("xls")}>
        {t("export_xls")}
      </Menu.Item>
      <Menu.Item key="csv" onClick={() => handleExport("csv")}>
        {t("export_csv")}
      </Menu.Item>
      <Menu.Item key="pdf" onClick={() => handleExport("pdf")}>
        {t("export_pdf")}
      </Menu.Item>
      <Menu.Item key="preview" onClick={() => handleExport("preview")}>
        {t("preview")}
      </Menu.Item>
      <Menu.Item key="print" onClick={() => handleExport("print")}>
        {t("print")}
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="ota-revenue-performance-report-container">
      
<div
  className="page-header page-top-icons mb-4"
  style={{ background: "#f8f9fa", padding: "15px 20px", borderRadius: "8px" }}
>
  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
    <div className="d-flex align-items-center gap-4" style={{ minWidth: "60%" }}>
      <h2
        className="mb-0 d-flex align-items-center"
        style={{
          minWidth: "350px", // Ensure space for long headings
          maxWidth: "500px", // Cap to prevent overflow
          flexShrink: 0, // Prevent shrinking
        }}
      >
        <TrendingUp size={22} className="text-primary me-2" />
        {t("ota_revenue_performance")}
      </h2>
      <div style={{ marginLeft: "auto" }}> {/* Shift DatePicker to the right */}
        <DatePicker
          initialSettings={{
            singleDatePicker: false,
            showDropdowns: true,
            startDate: dateRange.startDate.toDate(),
            endDate: dateRange.endDate.toDate(),
            ranges: {
              Today: [moment().startOf("day").toDate(), moment().startOf("day").toDate()],
              Yesterday: [
                moment().subtract(1, "days").startOf("day").toDate(),
                moment().subtract(1, "days").startOf("day").toDate(),
              ],
              "Last 7 Days": [
                moment().subtract(7, "days").startOf("day").toDate(),
                moment().startOf("day").toDate(),
              ],
              "Last 30 Days": [
                moment().subtract(30, "days").startOf("day").toDate(),
                moment().startOf("day").toDate(),
              ],
              "This Month": [
                moment().startOf("month").toDate(),
                moment().endOf("month").toDate(),
              ],
              "Last Month": [
                moment().subtract(1, "month").startOf("month").toDate(),
                moment().subtract(1, "month").endOf("month").toDate(),
              ],
            },
            locale: {
              format: "DD MMM YYYY",
              applyLabel: "Select",
              cancelLabel: "Cancel",
            },
          }}
          onCallback={(start, end) => handleDateRangeChange({ start, end })}
        >
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
              style={{
                width: "220px",
                height: "38px",
                fontSize: "16px",
                fontWeight: "bold",
                textAlign: "center",
                padding: "0 10px",
                borderRight: "none",
              }}
              readOnly
            />
            <div className="input-group-append">
              <span
                className="input-group-text"
                style={{
                  height: "38px",
                  backgroundColor: "#fff",
                  borderLeft: "none",
                  borderRadius: "0 4px 4px 0",
                  cursor: "pointer",
                }}
              >
                <i className="fa fa-chevron-down" style={{ color: "#495057" }} />
              </span>
            </div>
          </div>
        </DatePicker>
      </div>
    </div>
    <div className="d-flex align-items-center gap-3">
      <div className="d-flex align-items-center me-2">
        <span style={{ fontSize: "14px", color: "#666", marginRight: "5px" }}>{t("show_graph")}:</span>
        <Switch checked={showGraph} onChange={(checked) => setShowGraph(checked)} />
      </div>
      <Popover
        content={filterPopupContent}
        title={<span className="font-medium">{t("filters")}</span>}
        trigger="click"
        placement="bottomRight"
        overlayStyle={{ width: "250px" }}
      >
        <Button
          className="btn btn-link p-0"
          style={{ fontSize: "14px", color: "#007bff" }}
        >
          <Filter size={16} style={{ marginRight: "5px" }} />
          {t("filter")}
        </Button>
      </Popover>
      <Dropdown overlay={downloadMenu} trigger={["click"]}>
        <Button
          type="primary"
          style={{
            height: "38px",
            display: "flex",
            alignItems: "center",
            padding: "0 12px",
            fontSize: "14px",
          }}
        >
          <Download size={16} style={{ marginRight: "5px" }} />
          {t("download")}
        </Button>
      </Dropdown>
    </div>
  </div>
  <p className="mt-2 mb-0" style={{ color: "#666", fontFamily: "'Roboto', sans-serif" }}>
    {t("tracks_revenue_ota_partners")}
  </p>
</div>


          {loading ? (
            <div className="text-center my-5">
              <div className="loader">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div
              className="no-data-container text-center mt-4"
              style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
            >
              <img
                src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                alt="No data available"
                style={{ width: "150px", marginBottom: "20px" }}
              />
              <h3>{t("no_data")}</h3>
              <p>{t("no_ota_revenue_data")}</p>
            </div>
          ) : (
            <>
              {showGraph && (
                <div className="row mb-4">
                  <div className="col-md-12">
                    <div
                      className="card"
                      style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}
                    >
                      <div
                        className="card-header"
                        style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}
                      >
                        <h5
                          className="mb-0 d-flex align-items-center"
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#333",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {t("ota_revenue_performance_chart")} (Total Bookings: {totals.room_sold_count})
                        </h5>
                        <div
                          style={{
                            position: "absolute",
                            bottom: "0",
                            left: "0",
                            width: "100%",
                            height: "4px",
                            backgroundColor: "#28a745",
                          }}
                        />
                      </div>
                      <div className="card-body" style={{ padding: "10px", height: "300px" }}>
                        <div style={{ height: "100%", width: "100%" }}>
                          <Bar data={barChartData} options={barChartOptions} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="row g-0">
                <div className="col-md-12">
                  <div
                    className="card"
                    style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}
                  >
                    <div
                      className="card-header"
                      style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}
                    >
                      <h5
                        className="mb-0 d-flex align-items-center"
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#333",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("ota_revenue_details")} (Total Bookings: {totals.room_sold_count})
                      </h5>
                      <div
                        style={{
                          position: "absolute",
                          bottom: "0",
                          left: "0",
                          width: "100%",
                          height: "4px",
                          backgroundColor: "#007bff",
                        }}
                      />
                    </div>
                    <div className="card-body p-0">
                      <Table
                        columns={columns}
                        dataSource={filteredData}
                        rowKey="booking_source"
                        pagination={false}
                        bordered
                        className="daybook-table"
                        style={{ margin: 0 }}
                        summary={() => (
                          <Table.Summary.Row style={{ backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
                            <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
                            <Table.Summary.Cell>{totals.room_sold_count.toLocaleString()}</Table.Summary.Cell>
                            <Table.Summary.Cell>{totals.total_nights.toLocaleString()}</Table.Summary.Cell>
                            <Table.Summary.Cell>{currency}{totals.actual_price.toFixed(2)}</Table.Summary.Cell>
                            <Table.Summary.Cell>{currency}{totals.discount.toFixed(2)}</Table.Summary.Cell>
                            {applicableTaxes.map((taxName, index) => (
                              <Table.Summary.Cell key={index}>{currency}{totals[taxName].toFixed(2)}</Table.Summary.Cell>
                            ))}
                            <Table.Summary.Cell>{currency}{totals.total_tax.toFixed(2)}</Table.Summary.Cell>
                            <Table.Summary.Cell style={{ backgroundColor: "#e2e6ea" }}>
                              {currency}{totals.net_revenue.toFixed(2)}
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          <PrintReportPreview
            visible={previewVisible}
            onClose={() => setPreviewVisible(false)}
            reportTitle={reportTitle}
            propertyName=""
            data={exportData}
            headers={exportHeaders}
            footerData={totalsForPageFooter}
            totalColumns={totalColumns}
            apiEndpoint="transactions/generate-pdf"
            apiUrl={apiURL}
          />
          <style jsx>{`
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
            .loader {
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .dot {
              width: 10px;
              height: 10px;
              background-color: #007bff;
              border-radius: 50%;
              margin: 0 5px;
              animation: dot-bounce 1.4s infinite ease-in-out both;
            }
            .dot:nth-child(1) { animation-delay: -0.32s; }
            .dot:nth-child(2) { animation-delay: -0.16s; }
            @keyframes dot-bounce {
              0%, 80%, 100% { transform: scale(0); }
              40% { transform: scale(1); }
            }
            .daybook-table .ant-table-thead > tr > th {
              background: #ffffff;
              font-weight: 600;
              padding: 8px 16px;
              font-size: 14px;
            }
            .daybook-table .ant-table-tbody > tr > td {
              padding: 12px 18px;
              font-size: 14px;
              height: 60px;
            }
            .daybook-table .ant-table {
              border: 1px solid #dee2e6;
            }
            .daybook-table .ant-table-tbody > tr.ant-table-row:hover > td {
              background: #f5f5f5;
            }
            .colored-box.source-box {
              display: inline-block;
              padding: 4px 8px;
              border-radius: 4px;
              background-color: #e9ecef;
              color: #333;
            }
            .form-control {
              border-radius: 4px;
              border: 1px solid #d9d9d9;
              padding: 6px 12px;
              font-size: 14px;
            }
          `}</style>
        </div>
      </div>
    </div>
  );
};

export default OTARevenuePerformanceReport;